import React from 'react';
import { format, isValid } from 'date-fns';
import { ptBR, enUS } from 'date-fns/locale';
import { ReportHeader } from './ReportHeader';
import { TaskInfo } from './TaskInfo';
import { TaskMetadata } from './TaskMetadata';
import { TaskContent } from './TaskContent';
import { AnonymizedBadge } from '../AnonymizedBadge';
import { NameBadge } from '../NameBadge';
import { ReportCover } from './ReportCover';
import { summarizeTaskDescription, extractTimelineEvents, analyzeResults, translateToEnglish } from '../../services/chatGptApi';
import { ClickUpTask, ClickUpDoc } from '../../types/clickup';

interface TaskReportProps {
  task: ClickUpTask;
  doc?: ClickUpDoc | null;
  onBackToForm: () => void;
}

const TaskReport: React.FC<TaskReportProps> = ({ task: initialTask, doc, onBackToForm }) => {
  const [task, setTask] = React.useState<ClickUpTask>(initialTask);
  const [summary, setSummary] = React.useState<string | null>(null);
  const [summaryError, setSummaryError] = React.useState<string | null>(null);
  const [isLoadingSummary, setIsLoadingSummary] = React.useState(false);
  const [timelineEvents, setTimelineEvents] = React.useState<Array<{ date: string; event: string }>>([]);
  const [timelineError, setTimelineError] = React.useState<string | null>(null);
  const [isLoadingTimeline, setIsLoadingTimeline] = React.useState(false);
  const [results, setResults] = React.useState<string[] | null>(null);
  const [resultsError, setResultsError] = React.useState<string | null>(null);
  const [isLoadingResults, setIsLoadingResults] = React.useState(false);
  const [isEnglish, setIsEnglish] = React.useState(false);
  const [isTranslating, setIsTranslating] = React.useState(false);
  const [hideAttachments, setHideAttachments] = React.useState(false);
  const [showCover, setShowCover] = React.useState(false);
  const [translatedContent, setTranslatedContent] = React.useState<{
    taskName: string;
    summary: string | null;
    timelineEvents: Array<{ date: string; event: string }>;
    results: string[] | null;
    category: string | null;
    vertical: string | null;
    operation: string | null;
    team: string | null;
  } | null>(null);
  const [anonymizationOptions, setAnonymizationOptions] = React.useState<{
    operation: boolean;
    team: boolean;
    names: boolean;
  }>({
    operation: false,
    team: false,
    names: false
  });

  const [isFullyLoaded, setIsFullyLoaded] = React.useState(false);
  const [isRegenerating, setIsRegenerating] = React.useState(false);

  React.useEffect(() => {
    const aiContentLoaded = !isLoadingSummary && !isLoadingTimeline && !isLoadingResults;
    const hasRequiredContent = !!summary;
    const hasOptionalContent = !doc?.content || (!!timelineEvents.length && !!results);
    
    setIsFullyLoaded(aiContentLoaded && hasRequiredContent && hasOptionalContent);
  }, [isLoadingSummary, isLoadingTimeline, isLoadingResults, summary, timelineEvents, results, doc]);

  const generateSummary = async () => {
    if (!task.description) {
      setSummaryError('Não há descrição disponível para gerar o resumo');
      return;
    }

    setIsLoadingSummary(true);
    setSummaryError(null);
    setSummary(null);
    
    try {
      const result = await summarizeTaskDescription(task.description);
      setSummary(result);
    } catch (error) {
      setSummaryError(error instanceof Error ? error.message : 'Erro ao gerar resumo');
    } finally {
      setIsLoadingSummary(false);
    }
  };

  const processTimeline = async () => {
    if (!doc?.content) {
      setTimelineError('Não há conteúdo disponível no documento');
      return;
    }

    setIsLoadingTimeline(true);
    setTimelineError(null);
    setTimelineEvents([]);

    try {
      const events = await extractTimelineEvents(doc.content);
      setTimelineEvents(events);
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Erro ao processar a linha do tempo';
      setTimelineError(errorMessage);
    } finally {
      setIsLoadingTimeline(false);
    }
  };

  const processResults = async () => {
    if (!doc?.content) {
      setResultsError('Não há conteúdo disponível para análise');
      return;
    }

    setIsLoadingResults(true);
    setResultsError(null);
    setResults(null);

    try {
      const analysisResults = await analyzeResults(doc.content);
      setResults(analysisResults);
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Erro ao processar os resultados';
      setResultsError(errorMessage);
    } finally {
      setIsLoadingResults(false);
    }
  };

  const handleRegenerateReport = async () => {
    setIsRegenerating(true);
    
    // Reset all states
    setSummary(null);
    setSummaryError(null);
    setTimelineEvents([]);
    setTimelineError(null);
    setResults(null);
    setResultsError(null);
    
    try {
      // Generate all content in parallel
      await Promise.all([
        generateSummary(),
        doc?.content ? processTimeline() : Promise.resolve(),
        doc?.content ? processResults() : Promise.resolve()
      ]);
    } finally {
      setIsRegenerating(false);
    }
  };

  React.useEffect(() => {
    if (task.description) {
      generateSummary();
    }
    if (doc?.content) {
      processTimeline();
      processResults();
    }
  }, [task.description, doc?.content]);

  const handleTranslate = async () => {
    if (isTranslating) return;

    setIsTranslating(true);
    try {
      if (isEnglish) {
        setIsEnglish(false);
        setTranslatedContent(null);
      } else {
        const [
          translatedName,
          translatedSummary,
          translatedEvents,
          translatedResults,
          translatedCategory,
          translatedVertical,
          translatedOperation,
          translatedTeam
        ] = await Promise.all([
          translateToEnglish(task.name),
          summary ? translateToEnglish(summary) : Promise.resolve(null),
          Promise.all(timelineEvents.map(async event => ({
            date: event.date,
            event: await translateToEnglish(event.event)
          }))),
          results ? Promise.all(results.map(result => translateToEnglish(result))) : Promise.resolve(null),
          task.category ? translateToEnglish(task.category) : Promise.resolve(null),
          vertical ? translateToEnglish(vertical) : Promise.resolve(null),
          operation ? translateToEnglish(operation) : Promise.resolve(null),
          team ? translateToEnglish(team) : Promise.resolve(null)
        ]);

        setTranslatedContent({
          taskName: translatedName,
          summary: translatedSummary,
          timelineEvents: translatedEvents,
          results: translatedResults,
          category: translatedCategory,
          vertical: translatedVertical,
          operation: translatedOperation,
          team: translatedTeam
        });
        setIsEnglish(true);
      }
    } catch (error) {
      console.error('Translation error:', error);
    } finally {
      setIsTranslating(false);
    }
  };

  const handlePrint = (options: { showCover: boolean }) => {
    setShowCover(options.showCover);
    window.print();
  };

  const handleUpdateCategory = (newCategory: string) => {
    setTask(prevTask => ({
      ...prevTask,
      category: newCategory
    }));
  };

  const handleUpdatePriority = (newPriority: number) => {
    setTask(prevTask => ({
      ...prevTask,
      priority: newPriority
    }));
  };

  const handleUpdateTimeline = (newEvents: Array<{ date: string; event: string }>) => {
    setTimelineEvents(newEvents);
  };

  const formatDate = (dateString: string | number | null) => {
    if (!dateString) return null;
    
    const timestamp = typeof dateString === 'string' ? parseInt(dateString, 10) : dateString;
    const date = new Date(timestamp);
    
    if (!isValid(date)) return null;
    return format(date, "dd 'de' MMMM 'de' yyyy", { locale: isEnglish ? enUS : ptBR });
  };

  const findCustomFieldValue = (searchTerm: string): string | null => {
    const field = task.custom_fields?.find(field => 
      field.name === searchTerm
    );
    
    if (!field) return null;

    if (field.name === '2- Equipe') {
      return field.value || null;
    }

    if (field.type_config?.options) {
      if (Array.isArray(field.value)) {
        return field.type_config.options
          .filter(option => field.value.includes(option.id))
          .map(option => option.name)
          .join(', ');
      }
      
      if (typeof field.value === 'number' && field.type_config.options[field.value]) {
        return field.type_config.options[field.value].name;
      }

      if (typeof field.value === 'string') {
        const option = field.type_config.options.find(opt => opt.id === field.value);
        return option?.name || null;
      }
    }

    return null;
  };

  const findCustomFieldDate = (searchTerm: string): string | number | null => {
    const field = task.custom_fields?.find(field => 
      field.name === searchTerm
    );
    return field?.value || null;
  };

  const openingDate = findCustomFieldDate('6 - Data abertura demanda');
  const startDate = findCustomFieldDate('7 - Data início execução');
  const completionDate = findCustomFieldDate('8 - Data conclusão');
  const operation = findCustomFieldValue('5 - Operação');
  const team = findCustomFieldValue('2- Equipe');
  const vertical = findCustomFieldValue('3 - Vertical');

  const formattedOpeningDate = formatDate(openingDate);
  const formattedStartDate = formatDate(startDate);
  const formattedCompletionDate = formatDate(completionDate || task.date_done);

  const anonymizeValue = (value: string | null, type: keyof typeof anonymizationOptions): React.ReactNode => {
    if (!value || !anonymizationOptions[type]) return value || '';
    return <AnonymizedBadge text={type === 'operation' ? 'Operação' : 'Time'} />;
  };

  const anonymizeContent = React.useCallback((content: string): React.ReactNode => {
    if (!content || !anonymizationOptions.names) return content;
    
    const patterns = [
      /[A-Z][a-zÀ-ÿ]+(?: (?:da|de|do|dos|das|e))? [A-Z][a-zÀ-ÿ]+(?:(?: (?:da|de|do|dos|das|e))? [A-Z][a-zÀ-ÿ]+)*/g,
      /[A-Z][a-zÀ-ÿ]+ [A-Z]\./g,
      /[A-Z][a-zÀ-ÿ]+ (?:da|de|do|dos|das) [A-Z][a-zÀ-ÿ]+/g,
      /[A-Z][a-zÀ-ÿ]+ [A-Z][a-zÀ-ÿ]+/g,
      /(?<=^|\s|[.!?])[A-Z][a-zÀ-ÿ]{3,}(?=\s|$|[,.!?])/g,
      /[A-Z][a-zÀ-ÿ]{2,}[áàâãéèêíìîóòôõúùûüç][a-zÀ-ÿ]*/g,
      /(?<=^|\s|[.!?])(John|William|David|Michael|Robert|James|Mary|Elizabeth)(?=\s|$|[,.!?])/g,
      /[A-Z][a-zÀ-ÿ]+-[A-Z][a-zÀ-ÿ]+/g,
      /(?:Dr|Eng|Prof|Sr|Sra|Srta)\. [A-Z][a-zÀ-ÿ]+/g
    ];

    const parts = content.split(/(\n)/);
    
    return (
      <>
        {parts.map((part, index) => {
          if (part === '\n') return <br key={index} />;
          
          let lastIndex = 0;
          const elements: React.ReactNode[] = [];
          
          const matches: Array<{ index: number, length: number }> = [];
          patterns.forEach(pattern => {
            const regex = new RegExp(pattern, 'g');
            let match;
            while ((match = regex.exec(part)) !== null) {
              matches.push({
                index: match.index,
                length: match[0].length
              });
            }
          });
          
          matches.sort((a, b) => a.index - b.index);
          
          const filteredMatches = matches.reduce((acc, curr) => {
            const last = acc[acc.length - 1];
            if (!last || curr.index >= last.index + last.length) {
              acc.push(curr);
            }
            return acc;
          }, [] as Array<{ index: number, length: number }>);
          
          filteredMatches.forEach((match) => {
            if (match.index > lastIndex) {
              elements.push(part.slice(lastIndex, match.index));
            }
            elements.push(
              <NameBadge key={`name-${match.index}`} isEnglish={isEnglish} />
            );
            lastIndex = match.index + match.length;
          });
          
          if (lastIndex < part.length) {
            elements.push(part.slice(lastIndex));
          }
          
          return <span key={index}>{elements}</span>;
        })}
      </>
    );
  }, [anonymizationOptions.names, isEnglish]);

  const displayName = isEnglish && translatedContent ? translatedContent.taskName : task.name;
  const displaySummary = anonymizeContent(isEnglish && translatedContent ? translatedContent.summary || '' : summary || '');
  const displayTimelineEvents = (isEnglish && translatedContent ? translatedContent.timelineEvents : timelineEvents)
    .map(event => ({
      ...event,
      event: anonymizeContent(event.event)
    }));
  const displayResults = (isEnglish && translatedContent ? translatedContent.results : results)
    ?.map(result => anonymizeContent(result));
  const displayCategory = isEnglish && translatedContent ? translatedContent.category : task.category;
  const displayVertical = isEnglish && translatedContent ? translatedContent.vertical : vertical;
  const displayOperation = anonymizeValue(
    isEnglish && translatedContent ? translatedContent.operation : operation,
    'operation'
  );
  const displayTeam = anonymizeValue(
    isEnglish && translatedContent ? translatedContent.team : team,
    'team'
  );

  return (
    <div className="report-container">
      {showCover && (
        <ReportCover
          title={displayName}
          isEnglish={isEnglish}
        />
      )}
      <div className="bg-white rounded-lg shadow-lg p-8 max-w-4xl mx-auto print:shadow-none print:p-4 print:max-w-none">
        <ReportHeader
          onBackToForm={onBackToForm}
          onTranslate={handleTranslate}
          onPrint={handlePrint}
          onToggleAttachments={() => setHideAttachments(!hideAttachments)}
          onRegenerateReport={handleRegenerateReport}
          onAnonymizationChange={setAnonymizationOptions}
          anonymizationOptions={anonymizationOptions}
          hasAttachments={!!task.attachments?.length}
          hideAttachments={hideAttachments}
          isTranslating={isTranslating}
          isEnglish={isEnglish}
          isFullyLoaded={isFullyLoaded}
          isRegenerating={isRegenerating}
          taskName={displayName}
        />

        <TaskInfo
          name={displayName}
          operation={displayOperation}
          team={displayTeam}
          dates={{
            opening: formattedOpeningDate,
            start: formattedStartDate,
            completion: formattedCompletionDate,
          }}
          isEnglish={isEnglish}
        />

        <TaskMetadata
          assignees={task.assignees || []}
          vertical={displayVertical}
          category={displayCategory}
          priority={task.priority}
          isEnglish={isEnglish}
          summary={summary}
          onUpdateCategory={handleUpdateCategory}
          onUpdatePriority={handleUpdatePriority}
        />

        <TaskContent
          summary={displaySummary}
          summaryError={summaryError}
          isLoadingSummary={isLoadingSummary}
          timelineEvents={displayTimelineEvents}
          timelineError={timelineError}
          isLoadingTimeline={isLoadingTimeline}
          results={displayResults}
          resultsError={resultsError}
          isLoadingResults={isLoadingResults}
          attachments={task.attachments}
          hideAttachments={hideAttachments}
          isEnglish={isEnglish}
          onRefreshSummary={generateSummary}
          onRefreshTimeline={processTimeline}
          onRefreshResults={processResults}
          onUpdateTimeline={handleUpdateTimeline}
        />
      </div>
    </div>
  );
};

export default TaskReport;

export { TaskReport };